import React, { useState, useEffect, useCallback } from 'react';
import DistributionForm from './components/DistributionForm';
import DistributorDetails from './components/DistributorDetails';
import SharedHeader from '../../components/shared/SharedHeader';
import ProtectedPage from '../../components/shared/ProtectedPage';
import { usePermissions } from '../../utils/usePermissions';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import LoadingOverlay from '../../components/shared/LoadingOverlay';
import { apiClient } from '../../api/apiClient';
import { 
  GET_DISTRIBUTORS_URL, 
  CREATE_DISTRIBUTOR_URL,
  INACTIVATE_DISTRIBUTOR_URL,
  ACTIVATE_DISTRIBUTOR_URL,
  DELETE_DISTRIBUTOR_URL 
} from '../../api/distributor';
import { toast } from '../../components/shared/toast';
import en from '../../translations/en.json';
import EmptyState from '../../components/shared/EmptyState';
import Loader from '../../components/shared/Loader';
import LabelAvatarStack from '../../components/shared/LabelAvatarStack';
import './Distributor.css';

function Distributor({ 
  onSelectSong, 
  onSelectCase,
  selectedDistributor: externalSelectedDistributor,
  onSelectDistributor,
  onBackFromDistributor
}) {
  const { canCreate, canEdit, canDelete } = usePermissions();
  const canCreateDistributor = canCreate('distributor');
  const canEditDistributor = canEdit('distributor');
  const canDeleteDistributor = canDelete('distributor');

  const [distributors, setDistributors] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [internalSelectedDistributor, setInternalSelectedDistributor] = useState(null);
  const selectedDistributor = externalSelectedDistributor !== undefined ? externalSelectedDistributor : internalSelectedDistributor;

  const handleSelectDistributor = (dist) => {
    if (onSelectDistributor) {
      onSelectDistributor(dist);
    } else {
      setInternalSelectedDistributor(dist);
    }
  };

  const handleBackDistributor = () => {
    if (onBackFromDistributor) {
      onBackFromDistributor();
    } else {
      setInternalSelectedDistributor(null);
    }
  };

  const [isAddingDistribution, setIsAddingDistribution] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Modals state — Inactivate
  const [showConfirmInactivateModal, setShowConfirmInactivateModal] = useState(false);
  const [distributorToInactivate, setDistributorToInactivate] = useState(null);
  const [inactivateDependencyWarningOpen, setInactivateDependencyWarningOpen] = useState(false);
  const [pendingInactivateSongs, setPendingInactivateSongs] = useState([]);

  // Modals state — Activate / Reactivate
  const [showConfirmActivateModal, setShowConfirmActivateModal] = useState(false);
  const [distributorToActivate, setDistributorToActivate] = useState(null);

  // Modals state — Delete
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [distributorToDelete, setDistributorToDelete] = useState(null);
  const [deleteDependencyWarningOpen, setDeleteDependencyWarningOpen] = useState(false);
  const [pendingDeleteSongs, setPendingDeleteSongs] = useState([]);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const fetchDistributors = useCallback(async () => {
    try {
      setIsLoading(true);
      const url = `${GET_DISTRIBUTORS_URL}?page=${currentPage}&limit=20&search=${searchQuery}`;
      const response = await apiClient(url);
      if (response && response.distributors) {
        setDistributors(response.distributors);
        setTotalCount(response.totalCount || 0);

        // Keep selectedDistributor updated with latest backend data
        if (selectedDistributor) {
          const updated = response.distributors.find(d => d.id === selectedDistributor.id);
          if (updated) {
            setSelectedDistributor(updated);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch distributors:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, selectedDistributor]);

  useEffect(() => {
    fetchDistributors();
  }, [currentPage, searchQuery]);

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    setCurrentPage(1);
  };

  const handleSaveDistribution = async (newData) => {
    try {
      const response = await apiClient(CREATE_DISTRIBUTOR_URL, {
        method: 'POST',
        body: newData
      });
      if (response) {
        toast.success('Distributor created successfully!');
        setIsAddingDistribution(false);
        fetchDistributors();
      }
    } catch (err) {
      console.error('Failed to create distributor:', err);
      toast.error('Failed to create distributor: ' + (err.message || ''));
    }
  };

  // ────────────────────────────────────────────────
  //  INACTIVATE FLOW
  // ────────────────────────────────────────────────
  const handleInactivateDistributor = (dist) => {
    setDistributorToInactivate(dist);
    setShowConfirmInactivateModal(true);
  };

  const handleConfirmInactivate = async () => {
    setShowConfirmInactivateModal(false);
    if (!distributorToInactivate) return;
    try {
      setIsLoading(true);
      const response = await apiClient(INACTIVATE_DISTRIBUTOR_URL(distributorToInactivate.id), {
        method: 'PUT'
      });
      if (response && response.hasDependencies) {
        setPendingInactivateSongs(response.dependentSongs || []);
        setInactivateDependencyWarningOpen(true);
      } else {
        toast.success('Distributor inactivated successfully!');
        if (selectedDistributor?.id === distributorToInactivate.id) {
          setSelectedDistributor(prev => prev ? { ...prev, status: 'Inactive' } : null);
        }
        fetchDistributors();
        setDistributorToInactivate(null);
      }
    } catch (err) {
      console.error('Inactivate check failed:', err);
      toast.error('Failed to inactivate distributor: ' + (err.message || ''));
    } finally {
      setIsLoading(false);
    }
  };

  const proceedForceInactivate = async () => {
    setInactivateDependencyWarningOpen(false);
    if (!distributorToInactivate) return;
    try {
      setIsLoading(true);
      await apiClient(`${INACTIVATE_DISTRIBUTOR_URL(distributorToInactivate.id)}?force=true`, {
        method: 'PUT'
      });
      toast.success('Distributor and related song mappings inactivated successfully!');
      if (selectedDistributor?.id === distributorToInactivate.id) {
        setSelectedDistributor(prev => prev ? { ...prev, status: 'Inactive' } : null);
      }
      fetchDistributors();
      setDistributorToInactivate(null);
      setPendingInactivateSongs([]);
    } catch (err) {
      console.error('Force inactivate failed:', err);
      toast.error('Failed to deactivate relationships.');
    } finally {
      setIsLoading(false);
    }
  };

  // ────────────────────────────────────────────────
  //  ACTIVATE / REACTIVATE FLOW
  // ────────────────────────────────────────────────
  const handleActivateDistributor = (dist) => {
    setDistributorToActivate(dist);
    setShowConfirmActivateModal(true);
  };

  const handleConfirmActivate = async () => {
    setShowConfirmActivateModal(false);
    if (!distributorToActivate) return;
    try {
      setIsLoading(true);
      await apiClient(ACTIVATE_DISTRIBUTOR_URL(distributorToActivate.id), {
        method: 'PUT'
      });
      toast.success('Distributor reactivated successfully!');
      if (selectedDistributor?.id === distributorToActivate.id) {
        setSelectedDistributor(prev => prev ? { ...prev, status: 'Active' } : null);
      }
      fetchDistributors();
      setDistributorToActivate(null);
    } catch (err) {
      console.error('Activate failed:', err);
      toast.error('Failed to reactivate distributor: ' + (err.message || ''));
    } finally {
      setIsLoading(false);
    }
  };

  // ────────────────────────────────────────────────
  //  DELETE FLOW (Soft Delete)
  // ────────────────────────────────────────────────
  const handleDeleteDistributor = (dist) => {
    setDistributorToDelete(dist);
    setShowConfirmDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowConfirmDeleteModal(false);
    if (!distributorToDelete) return;
    try {
      setIsLoading(true);
      const response = await apiClient(DELETE_DISTRIBUTOR_URL(distributorToDelete.id), {
        method: 'DELETE'
      });
      if (response && response.hasDependencies) {
        setPendingDeleteSongs(response.dependentSongs || []);
        setDeleteDependencyWarningOpen(true);
      } else {
        toast.success('Distributor permanently deleted!');
        if (selectedDistributor?.id === distributorToDelete.id) {
          setSelectedDistributor(null);
        }
        fetchDistributors();
        setDistributorToDelete(null);
      }
    } catch (err) {
      console.error('Delete check failed:', err);
      toast.error('Failed to initiate delete: ' + (err.message || ''));
    } finally {
      setIsLoading(false);
    }
  };

  const proceedForceDelete = async () => {
    setDeleteDependencyWarningOpen(false);
    if (!distributorToDelete) return;
    try {
      setIsLoading(true);
      await apiClient(`${DELETE_DISTRIBUTOR_URL(distributorToDelete.id)}?force=true`, {
        method: 'DELETE'
      });
      toast.success('Distributor and related mappings permanently deleted!');
      if (selectedDistributor?.id === distributorToDelete.id) {
        setSelectedDistributor(null);
      }
      fetchDistributors();
      setDistributorToDelete(null);
      setPendingDeleteSongs([]);
    } catch (err) {
      console.error('Force delete failed:', err);
      toast.error('Failed to delete relationships.');
    } finally {
      setIsLoading(false);
    }
  };

  // ────────────────────────────────────────────────
  //  MAIN EXPORT FLOW
  // ────────────────────────────────────────────────
  const handleExportClick = async () => {
    try {
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 800));

      const response = await apiClient(`${GET_DISTRIBUTORS_URL}?export=true&search=${searchQuery}`);
      if (response && response.distributors) {
        const headers = ['Account Code', 'Account Email', 'Company Name', 'Status', 'Song Count', 'Outgoing %'];
        const rows = response.distributors.map(d => [
          d.distributor_code || '—',
          d.email,
          d.company,
          d.status,
          d.songCount,
          `${d.percentage}%`
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `distributors_export_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Distributors list exported successfully!');
      } else {
        toast.error('Failed to export distributors.');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Error exporting distributors data.');
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.ceil(totalCount / 20) || 1;

  return (
    <ProtectedPage module="distributor">
      <div className="distributor-page-wrapper">
      {/* Dynamic View rendering */}
      {selectedDistributor ? (
        <DistributorDetails 
          distItem={selectedDistributor} 
          onBack={handleBackDistributor}
          onInactivate={handleInactivateDistributor}
          onActivate={handleActivateDistributor}
          onDelete={handleDeleteDistributor}
          onUpdateSuccess={fetchDistributors}
          onSelectSong={onSelectSong}
          onSelectCase={onSelectCase}
          t={t}
        />
      ) : isAddingDistribution ? (
        <DistributionForm 
          isModal={false}
          existingEmails={distributors.map(d => d.email)}
          onCancel={() => setIsAddingDistribution(false)}
          onSave={handleSaveDistribution}
        />
      ) : (
        <>
          {/* Shared Header Controls — no placeholder alerts */}
          <SharedHeader
            title={t('distribution.title') || 'Distributors'}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            searchPlaceholder={t('distribution.searchPlaceholder') || 'Search distributors...'}
            resultCount={totalCount}
            resultLabel="Results"
            showSortFilter={true}
            onSortClick={null}
            onFilterClick={null}
            onExportClick={handleExportClick}
            onAddNewClick={canCreateDistributor ? () => setIsAddingDistribution(true) : null}
            addNewText={t('common.addNew') || 'Add New'}
            canAddNew={canCreateDistributor}
          />

          {/* Distributors Table */}
          <div className="song-table-container">
            <div className="song-table-header distributor-table-grid">
              <div>{t('distributor.colAccountMail') || 'Account mail'}</div>
              <div>{t('distributor.statusHeader') || 'Status'}</div>
              <div>{t('distributor.companyHeader') || 'Company'}</div>
              <div>{t('distributor.colSongCount') || 'Song Count'}</div>
              <div>{t('distributor.colLabels') || 'Labels'}</div>
              <div>{t('distributor.colOutgoing') || 'Outgoing'}</div>
              <div>{t('distributor.colNotesCases') || 'Notes / Cases'}</div>
              <div>{t('distributor.colConflicts') || 'Conflicts'}</div>
              <div>Actions</div>
            </div>

            <div className="song-table-body">
              {isLoading ? (
                <LoadingOverlay isLoading message="Fetching distributors, please wait." />
              ) : distributors.length > 0 ? (
                distributors.map((d) => (
                  <div 
                    key={d.id} 
                    className="song-row distributor-table-grid"
                    onClick={() => handleSelectDistributor(d)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="bold-text" style={{ textDecoration: 'underline' }}>{d.email}</div>
                    <div>
                      <span className={`status-pill ${d.status === 'Active' ? 'active' : 'inactive'}`}>
                        <span className="status-dot"></span>
                        {d.status}
                      </span>
                    </div>
                    <div>{d.company}</div>
                    <div>{d.songCount} {d.songCount === 1 ? 'Song' : 'Songs'}</div>
                    <div>
                      <LabelAvatarStack labels={d.labels || d.recordLabels} />
                    </div>
                    <div>{d.percentage}%</div>
                    <div>{d.notes}</div>
                    <div>{d.conflicts}</div>
                    <div style={{ display: 'flex', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
                      {d.status === 'Active' ? (
                        canEditDistributor && (
                          <button 
                            type="button" 
                            onClick={() => handleInactivateDistributor(d)}
                            title="Make Inactive"
                            style={{ padding: '6px', borderRadius: '6px', border: 'none', background: '#fef3c7', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706' }}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="8" y1="12" x2="16" y2="12" />
                            </svg>
                          </button>
                        )
                      ) : (
                        <>
                          {canEditDistributor && (
                            <button 
                              type="button" 
                              onClick={() => handleActivateDistributor(d)}
                              title="Reactivate Distributor"
                              style={{ padding: '6px', borderRadius: '6px', border: 'none', background: '#dcfce7', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                              </svg>
                            </button>
                          )}
                          {canDeleteDistributor && (
                            <button 
                              type="button" 
                              onClick={() => handleDeleteDistributor(d)}
                              title="Delete Distributor"
                              style={{ padding: '6px', borderRadius: '6px', border: 'none', background: '#fee2e2', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <EmptyState title="No Distributors Found" subtitle="No distributors match your current search or filter." hasSearch={true} />
              )}
            </div>
          </div>

          {/* Pagination Footer */}
          {totalCount > 0 && (
            <div className="ringtone-pagination-row">
              <span className="pagination-range-text">
                {(currentPage - 1) * 20 + 1}-{Math.min(currentPage * 20, totalCount)} of {totalCount}
              </span>
              <div className="pagination-controls-wrapper">
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  &lt;&lt;
                </button>
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
                  <button 
                    key={num}
                    type="button" 
                    className={`pager-btn ${currentPage === num ? 'active' : 'outlined'}`}
                    onClick={() => setCurrentPage(num)}
                  >
                    {num}
                  </button>
                ))}
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ─── Confirmation Modal — Primary Inactivate ─── */}
      <ConfirmationModal
        isOpen={showConfirmInactivateModal}
        type="warning"
        title="Inactivate Distributor"
        message={`Are you sure you want to make the distributor "${distributorToInactivate?.company}" inactive?`}
        confirmLabel={t('common.confirm') || 'Confirm'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        onConfirm={handleConfirmInactivate}
        onCancel={() => {
          setShowConfirmInactivateModal(false);
          setDistributorToInactivate(null);
        }}
      />

      {/* ─── Confirmation Modal — Primary Activate / Reactivate ─── */}
      <ConfirmationModal
        isOpen={showConfirmActivateModal}
        type="success"
        title="Reactivate Distributor"
        message={`Are you sure you want to reactivate the distributor "${distributorToActivate?.company}"?`}
        confirmLabel={t('common.confirm') || 'Confirm'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        onConfirm={handleConfirmActivate}
        onCancel={() => {
          setShowConfirmActivateModal(false);
          setDistributorToActivate(null);
        }}
      />

      {/* ─── Confirmation Modal — Primary Delete ─── */}
      <ConfirmationModal
        isOpen={showConfirmDeleteModal}
        type="danger"
        title="Delete Distributor"
        message="Are you sure you want to delete this distributor?"
        confirmLabel={t('common.confirm') || 'Confirm'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowConfirmDeleteModal(false);
          setDistributorToDelete(null);
        }}
      />

      {/* ─── Dependency Warning — Inactivate ─── */}
      {inactivateDependencyWarningOpen && (
        <div className="modal-backdrop-overlay" style={{ zIndex: 100 }}>
          <div className="modal-card-container" style={{ width: '500px', padding: '24px' }} role="dialog" aria-modal="true">
            <button 
              className="modal-close-icon-btn" 
              onClick={() => {
                setInactivateDependencyWarningOpen(false);
                setDistributorToInactivate(null);
                setPendingInactivateSongs([]);
              }}
              aria-label="Close dialog"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="modal-content-wrapper" style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
              <div className="modal-icon-container warning" style={{ backgroundColor: '#fef3c7', color: '#d97706', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="modal-text-content">
                <h3 className="modal-title-heading" style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>
                  Inactivate Distributor Warning
                </h3>
                <p className="modal-description-text" style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '4px' }}>
                  This distributor is currently assigned to the following active songs. Continuing will make these distributor relationships inactive as well.
                </p>
              </div>
            </div>

            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', marginBottom: '20px', background: '#f8fafc' }}>
              {pendingInactivateSongs.map((song) => (
                <div key={song.id} style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)' }}>{song.name}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Artist: {song.artist} | Album: {song.album}</div>
                </div>
              ))}
            </div>

            <p style={{ fontSize: '13px', color: '#d97706', fontWeight: '600', marginBottom: '20px' }}>
              Proceeding will inactivate this distributor and all related song assignments shown above.
            </p>

            <div className="modal-footer-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                className="modal-btn modal-btn-cancel" 
                onClick={() => {
                  setInactivateDependencyWarningOpen(false);
                  setDistributorToInactivate(null);
                  setPendingInactivateSongs([]);
                }}
                style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="modal-btn modal-btn-confirm warning"
                onClick={proceedForceInactivate}
                style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#d97706', color: '#fff', fontWeight: '600' }}
              >
                Confirm Inactivate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Dependency Warning — Delete ─── */}
      {deleteDependencyWarningOpen && (
        <div className="modal-backdrop-overlay" style={{ zIndex: 100 }}>
          <div className="modal-card-container" style={{ width: '500px', padding: '24px' }} role="dialog" aria-modal="true">
            <button 
              className="modal-close-icon-btn" 
              onClick={() => {
                setDeleteDependencyWarningOpen(false);
                setDistributorToDelete(null);
                setPendingDeleteSongs([]);
              }}
              aria-label="Close dialog"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="modal-content-wrapper" style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
              <div className="modal-icon-container danger" style={{ backgroundColor: '#fee2e2', color: '#ef4444', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="modal-text-content">
                <h3 className="modal-title-heading" style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>
                  Delete Distributor Warning
                </h3>
                <p className="modal-description-text" style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '4px' }}>
                  This distributor has assigned songs. Deleting this distributor will also remove these song distributor relationships. Are you sure you want to continue?
                </p>
              </div>
            </div>

            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', marginBottom: '20px', background: '#f8fafc' }}>
              {pendingDeleteSongs.map((song) => (
                <div key={song.id} style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)' }}>{song.name}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Artist: {song.artist} | Album: {song.album}</div>
                </div>
              ))}
            </div>

            <p style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600', marginBottom: '20px' }}>
              Deleting this distributor will also remove its relationship from these songs.
            </p>

            <div className="modal-footer-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                className="modal-btn modal-btn-cancel" 
                onClick={() => {
                  setDeleteDependencyWarningOpen(false);
                  setDistributorToDelete(null);
                  setPendingDeleteSongs([]);
                }}
                style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="modal-btn modal-btn-confirm danger"
                onClick={proceedForceDelete}
                style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#ef4444', color: '#fff', fontWeight: '600' }}
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for main export */}
      <LoadingOverlay isLoading={isExporting} message={t('common.exportingMessage') || 'Exporting data...'} />
      </div>
    </ProtectedPage>
  );
}

export default Distributor;
